// Cadastro de disparos (HIS-APP-notificacoes-007) — consignado-trabalhador.
//
// Todas as rotas exigem role GRANATECH. A 007 é só cadastro: o único envio
// daqui é o de teste (testarDisparo), que vai para destinos digitados na tela.
//
// Não confundir com `rotina-disparo.service.ts`, que fala com a API antiga
// (/rotinas-disparo) da tela NotificacoesConfig.

import {
  AcaoCatalogo,
  AgendamentoCatalogo,
  AtivarDisparoDto,
  AtualizarDisparoDto,
  CatalogoDisparo,
  CriarDisparoDto,
  CriarDisparoTemplateDto,
  ContextoDisparo,
  DisparoConfiguracao,
  DisparosTotais,
  DisparoTemplateItem,
  CombinadorFiltro,
  EventoCatalogo,
  FormatacaoCatalogo,
  ListarDisparoTemplatesParams,
  ListarDisparosParams,
  ModeloMeta,
  PaginaDisparos,
  PreviewBuscaDto,
  PreviewResultado,
  ProvedorCatalogo,
  RespostaEntidadesCatalogo,
  ResultadoTesteDisparo,
  TestarDisparoDto,
} from "@/models/consignado/disparo.model";
import { axiosConsignadoInstance } from "../axiosInstance";

/**
 * Enquanto as tabelas da 007 não existem no ambiente, a tela roda com dados
 * fictícios sem precisar do backend. O mock imita a API inteira (paginação,
 * filtros, controle de versão), então a tela, os hooks e o container são os
 * mesmos nos dois modos — desligar o flag basta para valer o servidor.
 */
const USE_MOCK_DISPAROS = import.meta.env.VITE_USE_DISPAROS_MOCKS === "true";

const BASE = "/api-consignado-trabalhador/v1/disparos";

// ---------------------------------------------------------------------------
// Catálogos
// ---------------------------------------------------------------------------

export const buscarEventos = async (): Promise<EventoCatalogo[]> => {
  const { data } = await axiosConsignadoInstance.get(`${BASE}/catalogo/eventos`);
  return data;
};

/**
 * GET /catalogo/entidades.
 *
 * O contrato mudou com o suporte a filtros em árvore: antes a resposta era o
 * array de entidades, agora é `{ combinadores, entidades }`. Aceitamos as duas
 * formas porque os ambientes não sobem juntos — dev já está no formato novo,
 * hml/prd não necessariamente. Sem isso, `catalogo.entidades` viraria o objeto
 * inteiro e todo `find` de contexto quebraria a tela.
 */
export const buscarEntidades = async (): Promise<RespostaEntidadesCatalogo> => {
  const { data } = await axiosConsignadoInstance.get(`${BASE}/catalogo/entidades`);
  if (Array.isArray(data)) {
    // Formato antigo: sem combinadores, o cadastro é a lista plana com E.
    return { combinadores: [CombinadorFiltro.E], entidades: data };
  }
  return {
    combinadores: data?.combinadores ?? [CombinadorFiltro.E],
    entidades: data?.entidades ?? [],
  };
};

export const buscarAcoes = async (): Promise<AcaoCatalogo[]> => {
  const { data } = await axiosConsignadoInstance.get(`${BASE}/catalogo/acoes`);
  return data;
};

export const buscarCanais = async (): Promise<ProvedorCatalogo[]> => {
  const { data } = await axiosConsignadoInstance.get(`${BASE}/catalogo/canais`);
  return data;
};

export const buscarFormatacoes = async (): Promise<FormatacaoCatalogo[]> => {
  const { data } = await axiosConsignadoInstance.get(`${BASE}/catalogo/formatacoes`);
  return data;
};

export const buscarAgendamentos = async (): Promise<AgendamentoCatalogo> => {
  const { data } = await axiosConsignadoInstance.get(`${BASE}/catalogo/agendamentos`);
  return data;
};

/** 404 quando o modelo não existe na conta do WhatsApp com esse nome e idioma. */
export const buscarModeloMeta = async (nome: string, idioma: string): Promise<ModeloMeta> => {
  const { data } = await axiosConsignadoInstance.get(
    `${BASE}/catalogo/modelos-meta/${encodeURIComponent(nome)}`,
    { params: { idioma } },
  );
  return data;
};

/**
 * Os seis catálogos em paralelo — é o que o formulário precisa pra abrir.
 * Um só falhar derruba a carga inteira de propósito: sem o catálogo
 * completo a tela ofereceria opções que o backend recusaria.
 */
export const buscarCatalogoDisparo = async (): Promise<CatalogoDisparo> => {
  if (USE_MOCK_DISPAROS) {
    const { mockBuscarCatalogo } = await import("@/mocks/disparos.mock");
    return mockBuscarCatalogo();
  }
  const [eventos, respostaEntidades, acoes, canais, formatacoes, agendamentos] = await Promise.all([
    buscarEventos(),
    buscarEntidades(),
    buscarAcoes(),
    buscarCanais(),
    buscarFormatacoes(),
    buscarAgendamentos(),
  ]);
  return {
    eventos,
    entidades: respostaEntidades.entidades,
    combinadores: respostaEntidades.combinadores,
    acoes,
    canais,
    formatacoes,
    agendamentos,
  };
};

// ---------------------------------------------------------------------------
// Configurações de disparo
// ---------------------------------------------------------------------------

export const listarDisparos = async (params?: ListarDisparosParams): Promise<PaginaDisparos> => {
  if (USE_MOCK_DISPAROS) {
    const { mockListarDisparos } = await import("@/mocks/disparos.mock");
    return mockListarDisparos(params);
  }
  const { data } = await axiosConsignadoInstance.get(BASE, {
    params: {
      pagina: params?.pagina ?? 1,
      limite: params?.limite ?? 20,
      // O backend recebe ativo como string 'true' | 'false'.
      ...(params?.ativo === undefined ? {} : { ativo: String(params.ativo) }),
      ...(params?.contexto ? { contexto: params.contexto } : {}),
      ...(params?.tipoGatilho ? { tipoGatilho: params.tipoGatilho } : {}),
    },
  });
  return data;
};

/**
 * GET /disparos/resumo — contagens da faixa de tags, numa consulta agregada.
 * Recebe o mesmo `contexto` da listagem para contar o conjunto que está na tela.
 */
export const resumirDisparos = async (contexto?: ContextoDisparo): Promise<DisparosTotais> => {
  if (USE_MOCK_DISPAROS) {
    const { mockResumirDisparos } = await import("@/mocks/disparos.mock");
    return mockResumirDisparos(contexto);
  }
  const { data } = await axiosConsignadoInstance.get(`${BASE}/resumo`, {
    params: { ...(contexto ? { contexto } : {}) },
  });
  return data;
};

export const obterDisparo = async (id: string): Promise<DisparoConfiguracao> => {
  if (USE_MOCK_DISPAROS) {
    const { mockObterDisparo } = await import("@/mocks/disparos.mock");
    return mockObterDisparo(id);
  }
  const { data } = await axiosConsignadoInstance.get(`${BASE}/${id}`);
  return data;
};

export const criarDisparo = async (dto: CriarDisparoDto): Promise<DisparoConfiguracao> => {
  if (USE_MOCK_DISPAROS) {
    const { mockCriarDisparo } = await import("@/mocks/disparos.mock");
    return mockCriarDisparo(dto);
  }
  const { data } = await axiosConsignadoInstance.post(BASE, dto);
  return data;
};

/** PUT substitui a configuração inteira e exige a versão que o GET devolveu. */
export const atualizarDisparo = async (
  id: string,
  dto: AtualizarDisparoDto,
): Promise<DisparoConfiguracao> => {
  if (USE_MOCK_DISPAROS) {
    const { mockAtualizarDisparo } = await import("@/mocks/disparos.mock");
    return mockAtualizarDisparo(id, dto);
  }
  const { data } = await axiosConsignadoInstance.put(`${BASE}/${id}`, dto);
  return data;
};

/** Ativar revalida o cadastro inteiro; se algo ficou incompleto, é recusado. */
export const ativarDisparo = async (
  id: string,
  dto: AtivarDisparoDto,
): Promise<DisparoConfiguracao> => {
  if (USE_MOCK_DISPAROS) {
    const { mockAtivarDisparo } = await import("@/mocks/disparos.mock");
    return mockAtivarDisparo(id, dto);
  }
  const { data } = await axiosConsignadoInstance.patch(`${BASE}/${id}/ativo`, dto);
  return data;
};

/** DELETE leva a versão no corpo — daí o `data` no config do axios. */
export const removerDisparo = async (id: string, versao: number): Promise<void> => {
  if (USE_MOCK_DISPAROS) {
    const { mockRemoverDisparo } = await import("@/mocks/disparos.mock");
    return mockRemoverDisparo(id, versao);
  }
  await axiosConsignadoInstance.delete(`${BASE}/${id}`, { data: { versao } });
};

/** Prévia da busca que a rotina faria. Nunca envia mensagem. */
export const previewDisparo = async (dto: PreviewBuscaDto): Promise<PreviewResultado> => {
  if (USE_MOCK_DISPAROS) {
    const { mockPreviewDisparo } = await import("@/mocks/disparos.mock");
    return mockPreviewDisparo(dto);
  }
  const { data } = await axiosConsignadoInstance.post(`${BASE}/preview`, dto);
  return data;
};

/**
 * Envio de teste pela execução manual (executar-agora com `teste`): só para os
 * destinos informados, com valores de exemplo — ninguém do público recebe.
 *
 * Um backend sem o modo teste ignora o corpo e executa o disparo para o
 * público real. A resposta sem `teste: true` vira erro aqui para a tela não
 * tratar isso como um teste que deu certo.
 */
export const testarDisparo = async (
  id: string,
  dto: TestarDisparoDto,
): Promise<ResultadoTesteDisparo> => {
  if (USE_MOCK_DISPAROS) {
    const { mockTestarDisparo } = await import("@/mocks/disparos.mock");
    return mockTestarDisparo(id, dto);
  }
  const { data } = await axiosConsignadoInstance.post(`${BASE}/${id}/executar-agora`, dto);
  if (data?.teste !== true || !Array.isArray(data?.canais)) {
    throw new Error(
      "Este ambiente ainda não tem o envio de teste: o backend respondeu como execução manual.",
    );
  }
  return data;
};

// ---------------------------------------------------------------------------
// Templates reutilizáveis
// ---------------------------------------------------------------------------

export const listarDisparoTemplates = async (
  params?: ListarDisparoTemplatesParams,
): Promise<DisparoTemplateItem[]> => {
  if (USE_MOCK_DISPAROS) {
    const { mockListarTemplates } = await import("@/mocks/disparos.mock");
    return mockListarTemplates(params);
  }
  const { data } = await axiosConsignadoInstance.get(`${BASE}/templates`, {
    params: {
      ...(params?.ativo === undefined ? {} : { ativo: String(params.ativo) }),
      ...(params?.contexto ? { contexto: params.contexto } : {}),
      ...(params?.canal ? { canal: params.canal } : {}),
      ...(params?.provedor ? { provedor: params.provedor } : {}),
    },
  });
  return data;
};

export const obterDisparoTemplate = async (id: string): Promise<DisparoTemplateItem> => {
  if (USE_MOCK_DISPAROS) {
    const { mockObterTemplate } = await import("@/mocks/disparos.mock");
    return mockObterTemplate(id);
  }
  const { data } = await axiosConsignadoInstance.get(`${BASE}/templates/${id}`);
  return data;
};

export const criarDisparoTemplate = async (
  dto: CriarDisparoTemplateDto,
): Promise<DisparoTemplateItem> => {
  if (USE_MOCK_DISPAROS) {
    const { mockCriarTemplate } = await import("@/mocks/disparos.mock");
    return mockCriarTemplate(dto);
  }
  const { data } = await axiosConsignadoInstance.post(`${BASE}/templates`, dto);
  return data;
};

export const atualizarDisparoTemplate = async (
  id: string,
  dto: CriarDisparoTemplateDto,
): Promise<DisparoTemplateItem> => {
  if (USE_MOCK_DISPAROS) {
    const { mockAtualizarTemplate } = await import("@/mocks/disparos.mock");
    return mockAtualizarTemplate(id, dto);
  }
  const { data } = await axiosConsignadoInstance.put(`${BASE}/templates/${id}`, dto);
  return data;
};

export const removerDisparoTemplate = async (id: string): Promise<void> => {
  if (USE_MOCK_DISPAROS) {
    const { mockRemoverTemplate } = await import("@/mocks/disparos.mock");
    return mockRemoverTemplate(id);
  }
  await axiosConsignadoInstance.delete(`${BASE}/templates/${id}`);
};
